/**
 * Renders a single unified egress-audit entry as a row: a protocol badge plus protocol-specific
 * detail (HTTP request line, or a stateful-connection summary with endpoint / bytes / duration /
 * state). Used by both the classic Network Audit card and the alt-layout audit list so HTTP and
 * TCP records render consistently in the same Allowed / Denied buckets.
 *
 * @module components/connectors/EgressEntryRow
 */

import { ArrowUpRight, ArrowDownRight, Globe, Database } from 'lucide-react';
import { clsx } from 'clsx';
import type { UnifiedEgressEntry, EgressProtocol, ConnectionState } from './unifiedEgress';

function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exp);
  return `${exp === 0 ? value : value.toFixed(1)} ${units[exp]}`;
}

function formatDuration(ms?: number): string {
  if (!ms || ms <= 0) return '0 ms';
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const totalSeconds = Math.round(seconds);
  return `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`;
}

/** Base pill styling shared by the protocol and state pills. */
const PILL_BASE =
  'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0';

/** High-contrast protocol pill classes — solid fills so they stay legible on any row background. */
function protocolPillClass(protocol: EgressProtocol): string {
  return protocol === 'http'
    ? 'bg-gray-600 text-white dark:bg-gray-500 dark:text-white'
    : 'bg-primary-600 text-white dark:bg-primary-500 dark:text-white';
}

/** High-contrast connection-state pill classes. */
function statePillClass(state: ConnectionState): string {
  switch (state) {
    case 'open':
      return 'bg-success-600 text-white';
    case 'failed':
      return 'bg-danger-600 text-white';
    default:
      return 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100';
  }
}

function ProtocolIcon({ protocol }: { protocol: EgressProtocol }) {
  const cls = 'h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400';
  return protocol === 'http' ? <Globe className={cls} /> : <Database className={cls} />;
}

export function EgressEntryRow({ entry }: { entry: UnifiedEgressEntry }) {
  const denied = entry.status === 'denied';
  const containerCls = denied
    ? 'bg-danger-50 dark:bg-red-900/30'
    : 'bg-success-50 dark:bg-green-900/30';
  const monoCls = denied
    ? 'text-danger-700 dark:text-red-300'
    : 'text-success-700 dark:text-green-300';

  return (
    <div
      className={`flex items-start justify-between gap-2 p-2 rounded-sm text-sm ${containerCls}`}
    >
      <div className="flex flex-col min-w-0 gap-0.5">
        {/* Primary line: protocol badge + target */}
        <div className="flex items-center gap-2 min-w-0">
          <ProtocolIcon protocol={entry.protocol} />
          <span className={clsx(PILL_BASE, protocolPillClass(entry.protocol))}>
            {entry.protocolLabel}
          </span>
          {entry.kind === 'http' ? (
            <span className={`font-mono text-xs break-all ${monoCls}`}>
              {entry.method} {entry.url}
            </span>
          ) : (
            <span className={`font-mono text-xs break-all ${monoCls}`} title={entry.endpoint}>
              {entry.endpoint}
            </span>
          )}
        </div>

        {/* Secondary metadata line */}
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {new Date(entry.timestampMs).toLocaleString()}
          {entry.kind === 'http' && entry.connectionId && (
            <>
              {' '}
              {'\u2022'} {entry.connectionName ?? `${entry.connectionId.substring(0, 8)}...`}
            </>
          )}
          {entry.kind === 'http' && entry.matchedRule && (
            <>
              {' '}
              {'\u2022'} Rule: {entry.matchedRule}
            </>
          )}
          {entry.kind === 'connection' && entry.database && (
            <>
              {' '}
              {'\u2022'} {entry.database}
            </>
          )}
          {entry.kind === 'connection' && entry.proxyLoginName && (
            <>
              {' '}
              {'\u2022'} {entry.proxyLoginName}
            </>
          )}
        </span>

        {/* Connection metrics (only meaningful once closed) */}
        {entry.kind === 'connection' &&
          entry.metricsAreFinal &&
          ((entry.bytesOut ?? 0) > 0 || (entry.bytesIn ?? 0) > 0) && (
            <div className="flex items-center gap-3 text-xs font-semibold text-gray-800 dark:text-gray-100 mt-0.5">
              <span className="flex items-center gap-1">
                <ArrowUpRight className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                {formatBytes(entry.bytesOut)} out
              </span>
              <span className="flex items-center gap-1">
                <ArrowDownRight className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                {formatBytes(entry.bytesIn)} in
              </span>
              <span>{formatDuration(entry.durationMs)}</span>
            </div>
          )}

        {/* Failure detail */}
        {entry.kind === 'connection' && entry.connState === 'failed' && entry.failureReason && (
          <span
            className="text-xs text-danger-600 dark:text-danger-400 wrap-break-word"
            title={entry.failureReason}
          >
            {entry.failureReason}
          </span>
        )}
      </div>

      {/* Right-side state pill for connections (Open / Closed / Failed) */}
      {entry.kind === 'connection' && (
        <span className={clsx(PILL_BASE, statePillClass(entry.connState))}>
          {entry.connState === 'open' ? 'Open' : entry.connState === 'failed' ? 'Failed' : 'Closed'}
        </span>
      )}
    </div>
  );
}
